import { useState, useEffect } from 'react';
import api from './api/axiosInstance.js';
import { Form, Card, Row, Col, Button, Image } from 'react-bootstrap';
import icLegalName from './assets/img/ic-building.svg';
import icNumber from './assets/img/ic-number.svg';
import icValidationError from './assets/img/ic-danger.svg';

const CompanyAddLegal = ({ showErrorMessage, setShowErrorMessage, formData, handleChange, setErrors, errors, handleLegalSubmit, isEditPage}) => {
    const [isEditing, setIsEditing] = useState(isEditPage ? false : true);
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values

    // ✅ Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!formData.legalName.trim()) newErrors.legalName = 'Legal Name is required.';
        if (!formData.vat.trim()) newErrors.vat = 'VAT is required.';

        if (Object.keys(newErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...newErrors }));
            setShowErrorMessage(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        const vatDuplicate = await checkVATDuplicate(); // ✅ Check VAT duplicate before saving
        if (vatDuplicate) {
            setShowErrorMessage(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return; // ❌ Stop save if duplicate found
        }

        if (typeof handleLegalSubmit === 'function') {
            await handleLegalSubmit(e);
            setShowErrorMessage(false);
            setIsEditing(false);
        } else {
            console.error('Error: handleLegalSubmit is not a function.');
        }
    };


    // ✅ Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        handleChange({ target: { name: 'formData', value: tempFormData } }); // Restore previous values
    };

    const checkVATDuplicate = async () => {
        if (!formData.vat.trim()) return false; // ✅ Skip check if empty

        try {
            const response = await api.post(
                `/api/companies/check-vat`,
                { vat: formData.vat }
            );

            if (response.data.exists && formData.vat !== tempFormData.vat) {
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    vat: 'This VAT number is already registered.',
                }));
                return true; // duplicate exists
            } else {
                setErrors((prevErrors) => {
                    const { vat, ...rest } = prevErrors;
                    return rest;
                });
                return false; // no duplicate
            }
        } catch (error) {
            console.error('Error checking VAT:', error);
            return false; // treat as no duplicate on error, or handle differently if needed
        }
    };


    useEffect(() => {
        const checkVAT = async () => {
            if (!isEditing) return;
            if (!formData.vat.trim()) return; // ✅ Skip API check if empty

            try {
                const response = await api.post(
                    `/api/companies/check-vat`,
                    { vat: formData.vat }
                );

                if (response.data.exists && formData.vat !== tempFormData.vat) {
                    setErrors((prevErrors) => ({
                        ...prevErrors,
                        vat: 'This VAT number is already registered.',
                    }));
                } else {
                    setErrors((prevErrors) => {
                        const { vat, ...rest } = prevErrors;
                        return rest;
                    });
                }
            } catch (error) {
                console.error('Error checking VAT:', error);
            }
        };

        checkVAT();
    }, [formData.vat, isEditing]);

    const handleVATChange = (e) => {
        handleChange(e); // update parent

        const { value } = e.target;
        setErrors((prev) => {
            const updated = { ...prev };
            if (value.trim()) {
                delete updated.vat;
            } else {
                updated.vat = 'VAT is required.';
            }
            return updated;
        });
    };


    const handleLegalNameChange = (e) => {
        handleChange(e); // update parent

        const { value } = e.target;
        setErrors((prev) => {
            const updated = { ...prev };
            if (value.trim()) {
            delete updated.legalName;
            } else {
            updated.legalName = 'Legal Name is required.';
            }
            return updated;
        });
    };

    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    <Row>
                        <Col md={12}>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3><span className="item-number">2.</span> Legal Info</h3>
                            <hr />
                        </Col>
                    </Row>
                    {isEditing ? (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    {/* Company Legal Name */}
                                    <Form.Group className="validated" controlId="legalName">
                                        <Form.Label>Legal Name <span>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="legalName"
                                            value={formData.legalName}
                                            onChange={handleLegalNameChange}
                                            onBlur={() => {
                                                if (!formData.legalName.trim()) {
                                                setErrors(prev => ({ ...prev, legalName: 'Legal Name is required.' }));
                                                } else {
                                                setErrors(prev => {
                                                    const updated = { ...prev };
                                                    delete updated.legalName;
                                                    return updated;
                                                });
                                                }
                                            }}
                                            isInvalid={!!errors.legalName}
                                            required 
                                            disabled={!isEditing}
                                        />
                                        {errors.legalName && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.legalName} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.legalName}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    {/* VAT */}
                                    <Form.Group className="validated" controlId="vat">
                                        <Form.Label>VAT Number <span>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="vat"
                                            value={formData.vat}
                                            onChange={handleVATChange}
                                            onBlur={() => {
                                                if (!formData.vat.trim()) {
                                                    setErrors(prev => ({ ...prev, vat: 'VAT is required.' }));
                                                } else {
                                                    setErrors(prev => {
                                                        const updated = { ...prev };
                                                        delete updated.vat;
                                                        return updated;
                                                    });
                                                }
                                            }}
                                            isInvalid={!!errors.vat}
                                            required
                                            disabled={!isEditing}
                                        />
                                        {errors.vat && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.vat} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.vat}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    {/* Duns */}
                                    <Form.Group controlId="duns">
                                        <Form.Label>Duns</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="duns"
                                            value={formData.duns}
                                            onChange={handleChange}
                                            disabled={!isEditing} // ✅ Disable input when not in edit mode
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    {/* Global ultimate */}
                                    <Form.Group controlId="globalUltimate">
                                        <Form.Label>Global ultimate</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="globalUltimate"
                                            value={formData.globalUltimate}
                                            onChange={handleChange}
                                            disabled={!isEditing} // ✅ Disable input when not in edit mode
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        // Not editing
                        <>
                            <Row className="mb-4">
                                <Col md={6}>
                                    {/* Company Legal Name */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icLegalName}
                                                alt="Legal Name"
                                            />
                                        </span>
                                        <span className="text">
                                            Legal Name:
                                            <br />
                                            <strong>{formData.legalName}</strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    {/* VAT */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icNumber}
                                                alt="VAT Number"
                                            />
                                        </span>
                                        <span className="text">
                                            VAT Number:
                                            <br />
                                            <strong>{formData.vat}</strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    {/* Duns */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icNumber}
                                                alt="Duns"
                                            />
                                        </span>
                                        <span className="text">
                                            Duns:
                                            <br />
                                            <strong>{formData.duns || "N/A"}</strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    {/* Global ultimate */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icNumber}
                                                alt="Global ultimate"
                                            />
                                        </span>
                                        <span className="text">
                                            Global ultimate:
                                            <br />
                                            <strong>
                                                {formData.globalUltimate || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

export default CompanyAddLegal;
